/**
 * One webhook endpoint: edit it, test it. Reached by clicking a row on
 * /webhooks — the list is for finding, this page is for changing, the same
 * split as Tools/ToolEdit and Agents/AgentEdit.
 *
 * TWO SECRETS WITH OPPOSITE POSTURES, restated because this page renders both:
 * `signingSecret` is OURS and is SHOWN — copyable, on every read, to every
 * role — because its whole purpose is to be pasted into the receiving service;
 * it cannot be edited, since the receiver holds a pasted copy that would
 * silently stop verifying. Custom header VALUES are the customer's credentials
 * and are write-only: reads carry names only, an untouched editor omits
 * `headers` from the PATCH, any touch sends the full replacement map, and a
 * clear needs an explicit confirmation tick.
 *
 * THE TEST IS A REAL DELIVERY, not a ping: the panel POSTs an init-shaped
 * payload to the stored URL, signed with THIS row's secret, custom headers
 * included — so the receiver can verify it with the exact code path production
 * will use. The session id is `panel-test-…` so the receiver can tell a
 * rehearsal from a live call.
 *
 * UNLIKE A TOOL, THE NAME IS EDITABLE. A tool's name is what the model calls
 * and what transcripts record; an endpoint's name is a label for humans, agents
 * reference the id, nothing downstream keys on it.
 */
import { useEffect, useState } from 'react'
import { webhooks, type PatchWebhookBody, type ToolTestResult, type WebhookView } from '@/api'
import { can } from '@/lib/auth'
import { hostOf } from '@/lib/tool-form'
import { webhookMessageFor } from '@/lib/webhook-form'
import { useAuth } from '@/components/auth-provider'
import { Link } from '@/components/app-sidebar'
import { WebhookGuide } from '@/components/webhook-guide'
import { fmtWhen } from '@/components/state-pill'
import { SnippetBox } from '@/components/voice/snippet-box'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Textarea } from '@/components/ui/textarea'
import {
  HeadersEditor,
  buildHeaders,
  headerRowsClean,
  headerRowsDirty,
  seedHeaderRows,
  type HeaderRow,
} from '@/components/headers-editor'

interface Draft {
  name: string
  url: string
  enabled: boolean
  headerRows: HeaderRow[]
  clearConfirmed: boolean
}

function draftFrom(row: WebhookView): Draft {
  return {
    name: row.name,
    url: row.url,
    enabled: row.enabled,
    headerRows: seedHeaderRows(row.headerNames),
    clearConfirmed: false,
  }
}

/** Client params a rehearsal forwards, pre-filled so the shape teaches itself. */
const TEST_PARAMS_SEED = '{\n  "userID": "test-user"\n}'

export function WebhookEditPage({ id }: { id: string }) {
  const { user } = useAuth()
  const [server, setServer] = useState<WebhookView | null>(null)
  const [draft, setDraft] = useState<Draft | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    webhooks
      .get(id, ac.signal)
      .then((row) => {
        setServer(row)
        setDraft(draftFrom(row))
        setLoadError(null)
      })
      .catch((e: unknown) => {
        if (ac.signal.aborted) return
        setLoadError(webhookMessageFor(e))
      })
    return () => ac.abort()
  }, [id])

  // ------------------------------------------------------------------- test
  const [testParams, setTestParams] = useState<string | null>(null)
  const [testResult, setTestResult] = useState<ToolTestResult | null>(null)
  const [testRunning, setTestRunning] = useState(false)
  const [testError, setTestError] = useState<string | null>(null)

  const runTest = () => {
    if (!server || testRunning) return
    let params: unknown
    try {
      params = JSON.parse(testParams ?? TEST_PARAMS_SEED)
    } catch {
      setTestError('The params are not valid JSON.')
      return
    }
    if (
      typeof params !== 'object'
      || params === null
      || Array.isArray(params)
      || Object.values(params).some((v) => typeof v !== 'string')
    ) {
      setTestError('The params must be a JSON object of string values, like client params are.')
      return
    }
    setTestRunning(true)
    setTestError(null)
    setTestResult(null)
    webhooks
      .test(server.id, params as Record<string, string>, AbortSignal.timeout(25_000))
      .then((res) => {
        setTestRunning(false)
        setTestResult(res)
      })
      .catch((e: unknown) => {
        setTestRunning(false)
        setTestError(webhookMessageFor(e))
      })
  }

  if (loadError) {
    return (
      <div className="min-w-0 max-w-4xl">
        <p className="err">{loadError}</p>
        <p className="mt-2 text-sm">
          <Link to="/webhooks" className="underline underline-offset-4">
            Back to the webhooks list
          </Link>
        </p>
      </div>
    )
  }
  if (!server || !draft) return <p className="loading">Loading…</p>

  const revoked = server.revokedAt !== null
  const readOnly = !can(user, 'write') || revoked

  const onSave = () => {
    if (saving || readOnly) return
    const headersTouched = headerRowsDirty(draft.headerRows, server.headerNames)
    if (headersTouched && !headerRowsClean(draft.headerRows, true)) {
      setSaveError('Fix the header rows first — see the messages beside them.')
      return
    }
    const clearing = headersTouched && draft.headerRows.length === 0 && server.headerNames.length > 0
    if (clearing && !draft.clearConfirmed) {
      setSaveError('Confirm clearing the stored headers first — see the note in the headers editor.')
      return
    }

    // Only what changed, PATCH-style.
    const body: PatchWebhookBody = {}
    if (draft.name.trim() !== server.name) body.name = draft.name.trim()
    if (draft.url.trim() !== server.url) body.url = draft.url.trim()
    if (draft.enabled !== server.enabled) body.enabled = draft.enabled
    // WRITE-ONLY SEMANTICS: untouched editor omits `headers` (stored values
    // survive), any touch sends the full map, a confirmed clear sends {}.
    if (headersTouched) body.headers = buildHeaders(draft.headerRows)

    if (Object.keys(body).length === 0) {
      setSaveError('Nothing changed.')
      return
    }
    setSaving(true)
    setSaveError(null)
    setSaved(false)
    // NO AbortController tied to this component — a write must survive
    // navigation, same rule as every save in this app. A timeout bounds it.
    webhooks
      .update(server.id, body, AbortSignal.timeout(20_000))
      .then((row) => {
        setSaving(false)
        setSaved(true)
        setServer(row)
        setDraft(draftFrom(row))
      })
      .catch((e: unknown) => {
        setSaving(false)
        setSaveError(webhookMessageFor(e))
      })
  }

  /** How a 2xx JSON answer would be read by a live init delivery. */
  const overrides =
    testResult?.ok && typeof testResult.body === 'object' && testResult.body !== null
      ? (testResult.body as Record<string, unknown>)
      : null
  const wouldOverride =
    overrides !== null
    && (typeof overrides['instructions'] === 'string' || 'greeting' in overrides || 'params' in overrides)

  return (
    <div className="min-w-0 max-w-4xl">
      {/* No "Test this endpoint" shortcut up here — it only scrolled to the test
          panel below, which has its own Run button (removed 2026-08-23, with its
          tool-page twin). */}
      <div className="mb-4 flex items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">
            {server.name}
            {revoked && <span className="pill pill-plain ml-2 align-middle">revoked</span>}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            <span className="mono">{server.id}</span> &middot; created {fmtWhen(server.createdAt)}
            {revoked && server.revokedAt ? <> &middot; revoked {fmtWhen(server.revokedAt)}</> : null}
          </p>
        </div>
        {/* The receiver's contract, one click from the form that wires it. */}
        <WebhookGuide />
      </div>

      {revoked && (
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Revoked: agents still pointing at this endpoint stopped firing it, and its secret
          died with it. The row stays readable so those references can be explained.
        </p>
      )}
      {!revoked && !can(user, 'write') && (
        <p className="mb-4 max-w-2xl text-sm text-muted-foreground">
          Read-only: editing an endpoint is a write, and this account is a{' '}
          <span className="mono">{user?.role}</span>. Everything below, signing secret
          included, is open to you — the server returns it to every role.
        </p>
      )}

      <section className="panel">
        <h2>
          Endpoint
          <span className="note">
            the name is a label for humans — agents reference the id, so renaming is safe
          </span>
        </h2>
        <div className="body flex flex-col gap-4">
          {/* The reversible off-switch, first — same idiom as the tool page.
              Not revocation: attached agents keep the reference, and flipping
              it back needs no ceremony. Revoke (the permanent, secret-burning
              end of life) still exists in the API; this page stopped offering
              it (product decision 2026-08-23 — rotation is create-and-repoint). */}
          <div className="flex items-center gap-3">
            <Switch
              id="whEnabled"
              checked={draft.enabled}
              onCheckedChange={(v) => setDraft({ ...draft, enabled: v })}
              disabled={readOnly}
            />
            <Label htmlFor="whEnabled" className="cursor-pointer">
              {draft.enabled ? 'Active' : 'Inactive'}
            </Label>
            <span className="text-xs text-muted-foreground">
              {draft.enabled
                ? 'fires for every agent that references it, from their next session'
                : 'agents keep the reference, but neither the init nor the end-of-session webhook fires until it is active again'}
            </span>
          </div>

          <div className="grid2">
            <div className="field">
              <Label htmlFor="whName">Name</Label>
              <Input
                id="whName"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                disabled={readOnly}
              />
            </div>
            <div className="field">
              <Label htmlFor="whUrl">Endpoint URL</Label>
              <Input
                id="whUrl"
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                className="mono"
                disabled={readOnly}
              />
              <p className="text-xs text-muted-foreground">
                HTTPS, reachable from the public internet. Private addresses and credentials
                in the URL are refused at save time and re-checked at fire time.
              </p>
            </div>
          </div>

          <div className="field">
            <SnippetBox code={server.signingSecret} caption="signing secret" />
            <p className="text-xs text-muted-foreground">
              Ours, minted when this endpoint was created. The receiver uses it to verify
              that each delivery&rsquo;s signature really came from us. It cannot be changed
              — the receiver holds a pasted copy that would silently stop verifying. To
              rotate, revoke this endpoint and create a new one.
            </p>
          </div>

          {!readOnly ? (
            <>
              <HeadersEditor
                idPrefix="whEdit"
                rows={draft.headerRows}
                onChange={(update) =>
                  setDraft((prev) => (prev ? { ...prev, headerRows: update(prev.headerRows) } : prev))
                }
                storedNames={server.headerNames}
                clearConfirmed={draft.clearConfirmed}
                onClearConfirmed={(on) => setDraft({ ...draft, clearConfirmed: on })}
              />
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" onClick={onSave} disabled={saving}>
                  {saving ? 'Saving…' : 'Save changes'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDraft(draftFrom(server))
                    setSaveError(null)
                    setSaved(false)
                  }}
                  disabled={saving}
                >
                  Discard
                </Button>
                <span className="text-xs text-muted-foreground">
                  {saved
                    ? 'Saved. Sessions already running keep the endpoint they started with; the next session gets this one.'
                    : 'Sessions already running keep the endpoint they started with; the next session gets this one.'}
                </span>
                {saveError && <p className="err w-full text-sm">{saveError}</p>}
              </div>
            </>
          ) : (
            <div className="field">
              <span className="label">custom headers</span>
              <span className="text-sm">
                {server.headerNames.length === 0 ? (
                  <span className="empty">none</span>
                ) : (
                  <>
                    <span className="mono">{server.headerNames.join(', ')}</span>{' '}
                    <span className="text-muted-foreground">
                      — names only; the values are write-only for every role.
                    </span>
                  </>
                )}
              </span>
            </div>
          )}
        </div>
      </section>

      {!readOnly && (
        <section className="panel">
          <h2>
            Test
            <span className="note">
              one real signed delivery to {hostOf(draft.url)}, fired from the panel with the
              stored headers — the browser never holds them
            </span>
          </h2>
          <div className="body flex flex-col gap-3">
            <div className="field">
              <Label htmlFor="whTestParams">Client params</Label>
              <Textarea
                id="whTestParams"
                value={testParams ?? TEST_PARAMS_SEED}
                onChange={(e) => setTestParams(e.target.value)}
                rows={4}
                className="max-w-2xl font-mono text-xs"
                spellCheck={false}
              />
              <p className="text-xs text-muted-foreground">
                Forwarded as <span className="mono">params</span>, exactly as a live session
                forwards the caller&rsquo;s URL params. The payload carries a{' '}
                <span className="mono">panel-test-…</span> session id and a sample prompt, and
                is signed with the secret above — the receiver can verify it with its
                production code path. The test uses the SAVED URL and headers; unsaved edits
                above are not what fires.
              </p>
            </div>

            <div>
              <Button size="sm" onClick={runTest} disabled={testRunning}>
                {testRunning ? 'Delivering…' : 'Send test delivery'}
              </Button>
            </div>

            {testError && <p className="err text-sm">{testError}</p>}

            {testResult && (
              <div className="flex flex-col gap-2">
                <p className="text-sm">
                  <span className={testResult.ok ? 'pill' : 'pill pill-plain text-ev-bad'}>
                    {testResult.status === null ? 'no response' : `HTTP ${testResult.status}`}
                  </span>{' '}
                  <span className="text-muted-foreground">
                    in {testResult.durationMs.toLocaleString('en-GB')} ms
                    {testResult.ok
                      ? wouldOverride
                        ? ' — a live init delivery would APPLY the overrides below.'
                        : ' — no overrides returned; a live call would proceed with the agent’s own prompt, greeting and params.'
                      : ' — a live delivery would be ignored and the call would proceed with the originals (init webhooks fail open).'}
                  </span>
                </p>
                {testResult.error && <p className="err text-sm">{testResult.error}</p>}
                {testResult.body !== null && (
                  <pre className="max-h-80 max-w-3xl overflow-auto rounded-md border bg-muted/50 p-3 text-xs">
                    {JSON.stringify(testResult.body, null, 2)}
                  </pre>
                )}
              </div>
            )}
          </div>
        </section>
      )}

    </div>
  )
}
